import React, { useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../../firebaseConfig";
import ArticleForm from "./ArticleForm";

function ManageArticles({ volumeId, issue, onClose, onSave }) {
  const [isArticleFormOpen, setIsArticleFormOpen] = useState(false);
  const [currentArticle, setCurrentArticle] = useState(null); // For editing
  const [currentArticleIndex, setCurrentArticleIndex] = useState(-1);

  // Sort articles by their 'order' property for consistent display
  const sortedArticles = issue.articles
    ? [...issue.articles].sort((a, b) => (a.order || 0) - (b.order || 0)) // Handle potential undefined order
    : [];

  const handleAddArticle = () => {
    setCurrentArticle(null);
    setCurrentArticleIndex(-1);
    setIsArticleFormOpen(true);
  };

  const handleEditArticle = (article, index) => {
    setCurrentArticle(article);
    setCurrentArticleIndex(index);
    setIsArticleFormOpen(true);
  };

  const handleDeleteArticle = async (articleIdToDelete) => {
    if (window.confirm("Are you sure you want to delete this article?")) {
      // Filter by articleId instead of index for stability
      const updatedArticles = (issue.articles || []).filter(
        (article) => article.articleId !== articleIdToDelete,
      );
      const issueRef = doc(db, `journalVolumes/${volumeId}/issues`, issue.id);
      try {
        await updateDoc(issueRef, { articles: updatedArticles });
        onSave(); // This will trigger a re-fetch in the parent
      } catch (error) {
        console.error("Error deleting article:", error);
        alert("Failed to delete article.");
      }
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-40">
        <div className="bg-slate-50 p-6 rounded-lg shadow-xl w-full max-w-3xl h-[90vh] flex flex-col">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-slate-800">
              Manage Articles for: {issue.title}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-800"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                ></path>
              </svg>
            </button>
          </div>

          <div className="flex justify-end mb-4">
            <button
              onClick={handleAddArticle}
              className="bg-sky-600 hover:bg-sky-700 text-white font-bold py-2 px-4 rounded text-sm shadow-md"
            >
              + Add Article
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {sortedArticles.length > 0 ? (
              <ul className="space-y-3">
                {sortedArticles.map((article, index) => (
                  <li // Use article.articleId as key for stable list rendering
                    key={article.articleId || index}
                    className="bg-white p-4 rounded-lg shadow border border-slate-200 flex justify-between items-start"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-lg font-semibold text-sky-900 truncate">
                        {article.title}
                      </p>
                      <p className="text-sm text-slate-600">
                        {article.authors.join(", ")}
                      </p>
                    </div>
                    <div className="flex-shrink-0 ml-4">
                      <button
                        onClick={() => handleEditArticle(article, index)}
                        className="text-sky-600 hover:text-sky-800 text-sm mr-3"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteArticle(article.articleId)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-center py-10">
                <p className="text-slate-500">
                  No articles have been added to this issue yet.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {isArticleFormOpen && (
        <ArticleForm
          volumeId={volumeId}
          issue={issue}
          article={currentArticle}
          articleIndex={currentArticleIndex}
          onClose={() => setIsArticleFormOpen(false)}
          onSave={onSave}
        />
      )}
    </>
  );
}

export default ManageArticles;
